import { Table, Typography } from "antd";
import { useIntl } from "react-intl";
import styles from "~components/AddPersonRolesModal/AddPersonRolesModal.module.css";
import { ImageTypes } from "~enums/imageTypes";
import { useHandlePagination } from "~hooks/useHandlePagination";
import { useGetAddPersonToRosterColumns } from "~pages/Roster/components/AddPlayerToRoster/hooks/useGetAddPersonToRosterColumns";
import useFetchRoster from "~pages/Roster/hooks/useFetchRoster";
import { findImageByTypes } from "~utilities/findImageByTypes.ts";
import type { RosterTableRow } from "../RosterTable/types/rosterTableRow";
import type { AddPlayerToRosterTableRow } from "./types/AddPlayerToRosterTableRow";

type Props = {
  entityId?: string;
  handleAdd: (value: unknown) => Promise<unknown>;
  fixtureId?: string;
  isAddAllowed: boolean;
  isLoading: boolean;
  tableData: RosterTableRow[];
};
const AddPlayerToFixtureRoster = ({ entityId, handleAdd, fixtureId, tableData, isAddAllowed, isLoading }: Props) => {
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination();
  const { formatMessage } = useIntl();

  const params: Record<string, string> = {
    include: ["entities", "seasons", "persons"].join(","),
    limit: pageSize.toString(),
    offset: offset.toString(),
  };

  const { data: roster, isLoading: isRosterLoading } = useFetchRoster({
    customParams: params,
    entityId,
  });

  const addPlayerToRosterTableData = roster?.map((item) => ({
    nameFullLocal: item.person.nameFullLocal,
    avatarUrl: findImageByTypes(item.person?.images, [ImageTypes.PersonHead])?.url,
    nationality: item.person.nationality,
    gender: item.person.gender,
    personId: item.personId,
    dob: item.person.dob,
    status: item.status,
    position: item.position,
    bib: item.bib,
    isCaptain: item.isCaptain,
  }));

  const handleAddPerson = async (tableRow: AddPlayerToRosterTableRow) => {
    await handleAdd({
      personId: tableRow.personId,
      bib: tableRow.bib,
      position: tableRow.position,
      isCaptain: tableRow.isCaptain,
    });
  };

  const columns = useGetAddPersonToRosterColumns(isRosterLoading || isLoading, handleAddPerson, isAddAllowed, tableData || [], fixtureId);

  return (
    <>
      <div className={styles.topBar}>
        <div className={styles.topLeftContainer}>
          <Typography.Title level={4} className={styles.title}>
            {formatMessage({ id: "persons" })}
          </Typography.Title>
        </div>
      </div>
      <Table
        size="small"
        dataSource={addPlayerToRosterTableData}
        rowKey="personId"
        loading={isRosterLoading}
        columns={columns}
        className={styles.tableContainer}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: pageSizeOptions,
          showQuickJumper: false,
          total: offset + (addPlayerToRosterTableData?.length ?? 0) + 1,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: () => formatMessage({ id: "pageN" }, { number: currentPage }),
        }}
      />
    </>
  );
};

export default AddPlayerToFixtureRoster;
